/**
 * Vocabulary configuration for all vocabulary types
 */

export const VOCAB_CONFIG = {
  'business-models': {
    title: '💼 Business Models',
    endpoint: '/api/business-models',
    columns: [
      { key: 'id', label: 'ID', width: '20%' },
      { key: 'description', label: 'Description', width: '35%' },
      { key: 'debt_capital_need', label: 'Debt Capital', width: '15%' },
      { key: 'typical_revenue', label: 'Revenue Model', width: '30%' }
    ],
    renderCell: (item, column) => {
      if (column.key === 'id') return <span className="code">{item.id}</span>;
      if (column.key === 'debt_capital_need') {
        const colorMap = { high: 'badge-red', medium: 'badge-orange', low: 'badge-green' };
        return <span className={`badge-small ${colorMap[item[column.key]] || 'badge-gray'}`}>{item[column.key] || 'N/A'}</span>;
      }
      return <span style={{fontSize: column.key === 'typical_revenue' ? '13px' : 'inherit'}}>{item[column.key]}</span>;
    }
  },
  'operating-sectors': {
    title: '🌐 Operating Sectors',
    endpoint: '/api/operating-sectors',
    columns: [
      { key: 'id', label: 'ID', width: '30%' },
      { key: 'display_name', label: 'Display Name', width: '30%' },
      { key: 'notes', label: 'Notes', width: '40%' }
    ],
    renderCell: (item, column) => {
      if (column.key === 'id') return <span className="code">{item.id}</span>;
      if (column.key === 'display_name') return <strong>{item.display_name}</strong>;
      return <span style={{fontSize: '14px'}}>{item[column.key]}</span>;
    }
  },
  'provider-types': {
    title: '🏢 Provider Types',
    endpoint: '/api/provider-types',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'description', label: 'Description', width: '45%' },
      { key: 'aka', label: 'Also Known As', width: '30%', sortable: false }
    ],
    renderCell: (item, column) => {
      if (column.key === 'id') return <span className="code">{item.id}</span>;
      if (column.key === 'aka') {
        return (
          <div>
            {item.aka?.map((aka, i) => (
              <span key={i} className="badge-small badge-teal">{aka}</span>
            ))}
          </div>
        );
      }
      return item[column.key];
    }
  },
  'investment-strategies': {
    title: '📈 Investment Strategies',
    endpoint: '/api/investment-strategies',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'description', label: 'Description', width: '45%' },
      { key: 'aka', label: 'Also Known As', width: '30%', sortable: false }
    ],
    renderCell: (item, column) => {
      if (column.key === 'id') return <span className="code">{item.id}</span>;
      if (column.key === 'aka') {
        return (
          <div>
            {item.aka?.map((aka, i) => (
              <span key={i} className="badge-small badge-teal">{aka}</span>
            ))}
          </div>
        );
      }
      return item[column.key];
    }
  },
  'credit-focus': {
    title: '🎯 Credit Focus',
    endpoint: '/api/credit-focus',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'display_name', label: 'Display Name', width: '25%' },
      { key: 'description', label: 'Description', width: '50%' }
    ]
  },
  'deal-nature': {
    title: '🤝 Deal Nature',
    endpoint: '/api/deal-nature',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'display_name', label: 'Display Name', width: '25%' },
      { key: 'description', label: 'Description', width: '50%' }
    ]
  },
  'component-type': {
    title: '🧩 Component Type',
    endpoint: '/api/component-type',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'display_name', label: 'Display Name', width: '25%' },
      { key: 'description', label: 'Description', width: '50%' }
    ]
  },
  'funding-instruments': {
    title: '💰 Funding Instruments',
    endpoint: '/api/funding-instruments',
    isHierarchical: true,
    hasTreeView: true
  },
  'instrument-specificity': {
    title: '🎯 Instrument Specificity',
    endpoint: '/api/instrument-specificity',
    columns: [
      { key: 'id', label: 'ID', width: '25%' },
      { key: 'display_name', label: 'Display Name', width: '25%' },
      { key: 'description', label: 'Description', width: '50%' }
    ]
  },
  'asset-classes': {
    title: '🏦 Asset Classes',
    endpoint: '/api/asset-classes',
    hasTreeView: true,
    columns: [
      { key: 'id', label: 'ID', width: '15%' },
      { key: 'display_name', label: 'Display Name', width: '15%' },
      { key: 'borrower_category', label: 'Borrower', width: '10%' },
      { key: 'collateral_type', label: 'Collateral', width: '10%' },
      { key: 'aka', label: 'Aliases', width: '15%', sortable: false },
      { key: 'children', label: 'Children', width: '15%', sortable: false },
      { key: 'description', label: 'Description', width: '20%' }
    ],
    renderCell: (item, column) => {
      if (column.key === 'id') return <span className="code">{item.id}</span>;
      if (column.key === 'display_name') return <strong>{item.display_name}</strong>;
      if (column.key === 'borrower_category') return <span className="badge-small badge-purple">{item.borrower_category}</span>;
      if (column.key === 'aka') {
        return (
          <div>
            {item.aka?.slice(0, 3).map((aka, i) => (
              <span key={i} className="badge-small badge-blue">{aka}</span>
            ))}
            {item.aka?.length > 3 && <span className="badge-small badge-gray">+{item.aka.length - 3}</span>}
          </div>
        );
      }
      if (column.key === 'children') {
        if (!item.children || Object.keys(item.children).length === 0) return null;
        const childKeys = Object.keys(item.children);
        return (
          <div>
            {childKeys.slice(0, 3).map((childKey, i) => {
              const childDesc = item.children[childKey]?.description || '';
              return (
                <span 
                  key={i} 
                  className="badge-small badge-orange" 
                  title={`${childKey}: ${childDesc}`}
                >
                  {childKey}
                </span>
              );
            })}
            {childKeys.length > 3 && <span className="badge-small badge-gray">+{childKeys.length - 3}</span>}
          </div>
        );
      }
      if (column.key === 'description') {
        return <span style={{fontSize: '13px'}} title={item.description}>{item.description}</span>;
      }
      return item[column.key];
    }
  }
};
